import styled from "styled-components";

import { controlFocusRing, controlTransition } from "../../../foundations/controlStyles";
import { yScrollbarStyles } from "../../../foundations/scrollbar";
import { GridSurface, YEAR_BOX_HEIGHT } from "./calendarStyles";
import type { OverlayMode } from "./CalendarNav";

export interface MonthYearOverlayProps {
  mode: OverlayMode;
  /** 현재 pane의 달(선택 표시 기준). */
  paneView: Date;
  /** 월 라벨 12개(로캘 short). */
  monthLabels: string[];
  /** 연 목록. */
  years: number[];
  /** 월 선택. */
  onPickMonth: (month: number) => void;
  /** 연 선택. */
  onPickYear: (year: number) => void;
}

/**
 * 달력 드릴다운의 월/연 선택 grid(RFC-date-picker). 월(3열)·연(4열, 스크롤) 옵션 목록. 헤더의 연/월 버튼은
 * 조합자(Calendar)가 `CalendarNav`로 그리고, 이 컴포넌트는 grid 본문만 책임진다.
 */
export const MonthYearOverlay = ({
  mode,
  paneView,
  monthLabels,
  years,
  onPickMonth,
  onPickYear,
}: MonthYearOverlayProps) => (
  <GridSurface>
    {mode === "months" ? (
      <OptionGrid role="listbox" aria-label="Select month" $cols={3}>
        {monthLabels.map((ml, m) => {
          const selected = m === paneView.getMonth();
          return (
            <OptionButton
              key={m}
              type="button"
              role="option"
              aria-selected={selected}
              $selected={selected}
              onClick={() => onPickMonth(m)}
            >
              {ml}
            </OptionButton>
          );
        })}
      </OptionGrid>
    ) : (
      <OptionGrid role="listbox" aria-label="Select year" $cols={4}>
        {years.map((year) => {
          const selected = year === paneView.getFullYear();
          return (
            <OptionButton
              key={year}
              type="button"
              role="option"
              aria-selected={selected}
              $selected={selected}
              onClick={() => onPickYear(year)}
            >
              {year}
            </OptionButton>
          );
        })}
      </OptionGrid>
    )}
  </GridSurface>
);

// months(3열) / years(4열) 공용 옵션 그리드. 연도는 스크롤, 월(12개)은 스크롤 거의 없음.
const OptionGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  gap: 0.25rem;
  max-height: calc(${YEAR_BOX_HEIGHT} * 6);
  overflow-y: auto;
  ${yScrollbarStyles}
`;

const OptionButton = styled.button<{ $selected: boolean }>`
  border: none;
  height: ${YEAR_BOX_HEIGHT};
  padding: 0 0.5rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  color: ${({ theme, $selected }) =>
    $selected ? theme.color.text.onControl : theme.color.text.default};
  background: ${({ theme, $selected }) =>
    $selected ? theme.color.bg.control.primary : "transparent"};
  cursor: pointer;
  ${controlTransition("background-color", "color", "transform")}

  /* day 셀과 동일한 hover: 선택=solid hover, 그 외=surfaceRaisedHigh + 위로 살짝 lift. */
  &:hover {
    background: ${({ theme, $selected }) =>
      $selected ? theme.color.bg.control.primaryHover : theme.color.bg.surfaceRaisedHigh};
    transform: translateY(-2px);
  }
  &:focus-visible {
    ${controlFocusRing()}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;
